import { useEffect, useMemo, useRef, useState } from 'react';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import type { DocumentCodeSuggestion } from '@web/vin-sessions/types';
import { DOCUMENT_CODE_MIN_QUERY, DOCUMENT_CODE_PAGE_SIZE, normalizeDocumentCode } from '../document-code.utils';

const logger = getLogger('vin-sessions');

const DEBOUNCE_MS = 250;

export interface DocumentCodeScope {
    documentTypeIds?: string[];
    carlineId?: string;
    modelYear?: number;
}

interface Params {
    query: string;
    scope: DocumentCodeScope;
    enabled: boolean;
    browseWhenShort?: boolean;
}

export interface DocumentCodeLookup {
    suggestions: DocumentCodeSuggestion[];
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
}

export const useDocumentCodeSuggestions = ({
    query,
    scope,
    enabled,
    browseWhenShort = false,
}: Params): DocumentCodeLookup => {
    const [pages, setPages] = useState<DocumentCodeSuggestion[][]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [attempt, setAttempt] = useState(0);

    const normalized = normalizeDocumentCode(query);
    const scopeKey = [(scope.documentTypeIds ?? []).join(','), scope.carlineId ?? '', scope.modelYear ?? ''].join('|');
    const tooShort = normalized.length < DOCUMENT_CODE_MIN_QUERY;
    const willFetch = enabled && (!tooShort || browseWhenShort);

    const resetKey = `${normalized}|${scopeKey}|${enabled}`;
    const lastResetKey = useRef(resetKey);
    if (lastResetKey.current !== resetKey) {
        lastResetKey.current = resetKey;
        setPages([]);
        setOffset(0);
        setHasMore(false);
        setIsLoading(willFetch);
    }

    useEffect(() => {
        if (!willFetch) {
            setPages([]);
            setHasMore(false);
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);
        const controller = new AbortController();
        const timer = setTimeout(() => {
            docsService
                .suggestDocumentCodes({
                    query: tooShort ? '' : normalized,
                    documentTypeIds: scope.documentTypeIds,
                    carlineId: scope.carlineId,
                    modelYear: scope.modelYear,
                    limit: DOCUMENT_CODE_PAGE_SIZE,
                    offset,
                    signal: controller.signal,
                })
                .then((response) => {
                    if (controller.signal.aborted) return;
                    if (response.success) {
                        const page = response.data.suggestions ?? [];
                        const index = offset / DOCUMENT_CODE_PAGE_SIZE;
                        setPages((current) => {
                            const next = current.slice(0, index);
                            next[index] = page;
                            return next;
                        });
                        setHasMore(Boolean(response.data.hasMore));
                    }
                })
                .catch((error) => logger.error('Failed to fetch the document code suggestions', { error }))
                .finally(() => {
                    if (!controller.signal.aborted) setIsLoading(false);
                });
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeKey stands in for scope
    }, [willFetch, normalized, scopeKey, offset, attempt]);

    const suggestions = useMemo(() => pages.flat(), [pages]);

    return {
        suggestions,
        isLoading,
        hasMore,
        loadMore: () => {
            if (isLoading) return;
            setOffset(pages.length * DOCUMENT_CODE_PAGE_SIZE);
            // Bumped so a failed page, whose offset does not change, is asked for again.
            setAttempt((current) => current + 1);
        },
    };
};
